import React, { useState } from 'react';
import ModeTabs from './ModeTabs';
import AssignmentsTable from './AssignmentsTable';
import { ROLE_LIMITS, WEAPON_ROLE_OPTIONS } from './constants';
import { buildPlayingFiveCopy, getRoleStatusTone } from './helpers';
import { usePlayerRoles } from './usePlayerRoles';
import './styles.css';
import { toast } from 'react-toastify';

const toneStyles = {
  available: { border: '1px solid rgba(74, 222, 128, 0.25)', color: '#86efac', background: 'rgba(74, 222, 128, 0.08)' },
  full: { border: '1px solid rgba(250, 204, 21, 0.25)', color: '#fde68a', background: 'rgba(250, 204, 21, 0.08)' },
  invalid: { border: '1px solid rgba(248, 113, 113, 0.28)', color: '#fca5a5', background: 'rgba(248, 113, 113, 0.08)' },
};

const PlayerRoles = ({ teamId, uid, selectedSeason }) => {
  const [activeMode, setActiveMode] = useState('HP');
  const {
    loading,
    saving,
    activePlayers,
    playerImages,
    assignmentsByMode,
    validations,
    updateAssignment,
    resetMode,
    clearMode,
    saveAssignments,
  } = usePlayerRoles({ teamId, uid, selectedSeason });

  const activeRows = assignmentsByMode[activeMode] || [];
  const activeValidation = validations[activeMode] || { errors: [], roleUsage: {}, isValid: true };

  const handleCopyPlayingFive = async () => {
    const text = buildPlayingFiveCopy(activeMode, activeRows);
    if (!text.trim()) {
      toast.info('No starter lineup available to copy yet.');
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${activeMode} playing 5 copied.`);
    } catch (err) {
      console.error('Failed to copy playing 5', err);
      toast.error('Failed to copy playing 5');
    }
  };

  return (
    <section className="player-roles-shell">
      <div className="player-roles-card">
        <div className="player-roles-header">
        <div>
          <h2 className="player-roles-title">Player Assignments</h2>
          <p className="player-roles-subtitle">
            Store each player&apos;s primary and secondary weapon class roles, operator skills, and tactical jobs for the current season.
          </p>
        </div>
        <div className="player-roles-actions">
          <button
            type="button"
            onClick={handleCopyPlayingFive}
            className="player-roles-btn"
            disabled={loading}
          >
            Copy Playing 5
          </button>
          <button
            type="button"
            onClick={() => resetMode(activeMode)}
            className="player-roles-btn"
          >
            Refresh Roster
          </button>
          <button
            type="button"
            onClick={() => clearMode(activeMode)}
            className="player-roles-btn danger"
          >
            Clear {activeMode}
          </button>
          <button
            type="button"
            onClick={saveAssignments}
            disabled={saving || loading}
            className="player-roles-btn primary"
          >
            {saving ? 'Saving...' : 'Save Assignments'}
          </button>
        </div>
        </div>

        <div className="player-roles-body">
          <ModeTabs activeMode={activeMode} onChange={setActiveMode} />

          <div className="player-roles-layout">
            <div>
              {loading ? (
                <div className="player-roles-loading">Loading player assignments...</div>
              ) : (
                <AssignmentsTable
                  mode={activeMode}
                  rows={activeRows}
                  activePlayers={activePlayers}
                  playerImages={playerImages}
                  onChange={(rowIndex, field, value) => updateAssignment(activeMode, rowIndex, field, value)}
                />
              )}
            </div>

            <aside className="player-roles-sidebar">
              <div className="player-roles-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 className="player-roles-panel-title">Weapon Role Pool</h3>
                  <span className="player-roles-status" style={{ color: activeValidation.isValid ? '#86efac' : '#fca5a5' }}>
                    {activeValidation.isValid ? 'Valid' : 'Invalid'}
                  </span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {WEAPON_ROLE_OPTIONS.map((role) => {
                    const count = activeValidation.roleUsage[role.value] || 0;
                    const tone = getRoleStatusTone(role.value, count);
                    return (
                      <div
                        key={role.value}
                        className="player-roles-role-pill"
                        style={toneStyles[tone]}
                      >
                        <span>{role.label}</span>
                        <strong>{count}/{ROLE_LIMITS[role.value]}</strong>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="player-roles-panel">
                <h3 className="player-roles-panel-title">Validation Notes</h3>
                <div className="player-roles-notes">
                  <div>{activePlayers.length} active roster players detected.</div>
                  <div>Players cannot have the same primary and secondary role.</div>
                  <div>Only 5 players can be marked as starters. The extra assigned player should be marked as sub.</div>
                  <div>Hardpoint and Control use 2 tactical utility dropdowns. SND uses 3 tactical utility dropdowns.</div>
                  {!activeValidation.errors.length ? (
                    <div style={{ color: '#86efac' }}>
                      {activeValidation.starterCount || 0} starters, {activeValidation.subCount || 0} subs. No rule conflicts in {activeMode} right now.
                    </div>
                  ) : (
                    activeValidation.errors.map((error) => (
                      <div key={error} style={{ color: '#fca5a5' }}>{error}</div>
                    ))
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlayerRoles;
